<template>
  <div class="gg-field" :class="{ 'gg-field--wide': isFullWidth }">
    <label class="gg-field__label" :for="inputId" :title="field.tip || ''">
      {{ field.label || field.name }}
    </label>

    <!-- Boolean -->
    <div v-if="field.type === 'boolean'" class="gg-field__switch">
      <v-switch
        :input-value="value"
        @change="$emit('input', $event)"
        color="#98d8fe"
        hide-details
        dense
      />
    </div>

    <!-- Enum (integer with a fixed option list) -->
    <div v-else-if="isEnum" class="gg-field__box">
      <select
        :id="inputId"
        class="gg-field__control gg-field__control--select"
        :value="value"
        @change="$emit('input', Number($event.target.value))"
      >
        <option
          v-for="option in field.constraints.options"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <span class="gg-field__chevron" aria-hidden="true">
        <v-icon size="20">mdi-menu-down</v-icon>
      </span>
    </div>

    <!-- Numeric -->
    <div v-else-if="isNumeric" class="gg-field__box">
      <input
        :id="inputId"
        class="gg-field__control"
        type="number"
        :value="value"
        :placeholder="field.defaultValue != null ? String(field.defaultValue) : ''"
        :min="numericConstraint('minimum')"
        :max="numericConstraint('maximum')"
        :step="field.type === 'number' ? 'any' : 1"
        @input="$emit('input', Number($event.target.value))"
      />
    </div>

    <!-- Image / media URL: trailing accent button opens the image picker -->
    <div v-else-if="field.type === 'image'" class="gg-field__box">
      <input
        :id="inputId"
        class="gg-field__control"
        type="text"
        :value="value"
        placeholder="Cole a URL da imagem"
        @input="$emit('input', $event.target.value)"
      />
      <button
        type="button"
        class="gg-field__trailing"
        title="Buscar imagem"
        @click.stop="$emit('open-image-picker', field.name)"
      >
        <v-icon size="24">mdi-image-search-outline</v-icon>
      </button>
    </div>

    <!-- Duplet / triplet: one box per axis -->
    <div v-else-if="isComposite" class="gg-field__composite">
      <div v-for="(axis, index) in compositeAxes" :key="axis" class="gg-field__box">
        <input
          class="gg-field__control"
          type="number"
          :value="getCompositeValue(index)"
          :placeholder="axis"
          :maxlength="textConstraint('maxlength')"
          @input="setCompositeValue(index, $event.target.value)"
        />
      </div>
    </div>

    <!-- Multi-line text -->
    <div v-else-if="field.type === 'multiline'" class="gg-field__box gg-field__box--area">
      <textarea
        :id="inputId"
        class="gg-field__control gg-field__control--area"
        rows="2"
        :class="{ 'gg-field__control--upper': isUppercase }"
        :value="value"
        :placeholder="field.defaultValue || ''"
        :maxlength="textConstraint('maxlength')"
        @input="onTextInput($event.target.value)"
      />
    </div>

    <!-- Rich text keeps its dedicated editor; the label above is ours -->
    <GloboGraphicsRichTextEditor
      v-else-if="field.type === 'richtext'"
      :value="value"
      @input="$emit('input', $event)"
    />

    <!-- Default: single-line text -->
    <div v-else class="gg-field__box">
      <input
        :id="inputId"
        class="gg-field__control"
        :class="{ 'gg-field__control--upper': isUppercase }"
        type="text"
        :value="value"
        :placeholder="field.defaultValue || ''"
        :maxlength="textConstraint('maxlength')"
        :title="field.tip || ''"
        @input="onTextInput($event.target.value)"
      />
    </div>
  </div>
</template>

<script>
/**
 * One editable Vizrt template field: label above, control below. Field tips are
 * exposed as the control's tooltip rather than helper text, so every field keeps
 * the same height and the grid stays on a single rhythm.
 */
import GloboGraphicsRichTextEditor from "./GloboGraphicsRichTextEditor.vue";

export default {
  name: "GloboGraphicsFieldRenderer",
  components: { GloboGraphicsRichTextEditor },
  props: {
    field: { type: Object, required: true },
    value: { default: "" },
  },
  computed: {
    inputId() {
      return `gg-field-${this.field.name}`;
    },
    isEnum() {
      return (
        (this.field.type === "integer" || this.field.type === "number") &&
        !!this.field.constraints?.options
      );
    },
    isNumeric() {
      return ["number", "integer", "float"].includes(this.field.type);
    },
    isComposite() {
      return this.field.type === "duplet" || this.field.type === "triplet";
    },
    compositeAxes() {
      return this.field.type === "triplet" ? ["X", "Y", "Z"] : ["X", "Y"];
    },
    isUppercase() {
      return !!this.field.constraints?.uppercase;
    },
    // Controls that can't fit a half-width cell span the whole grid row.
    isFullWidth() {
      return ["richtext", "multiline", "duplet", "triplet"].includes(this.field.type);
    },
  },
  methods: {
    numericConstraint(key) {
      const bound = this.field.constraints?.[key];
      return bound != null ? bound : undefined;
    },
    textConstraint(key) {
      return this.field.constraints?.[key] || undefined;
    },
    onTextInput(val) {
      this.$emit("input", this.isUppercase ? (val || "").toUpperCase() : val);
    },
    getCompositeValue(index) {
      return String(this.value || "").split(";")[index] || "";
    },
    setCompositeValue(index, val) {
      const size = this.compositeAxes.length;
      const parts = String(this.value || "").split(";").slice(0, size);
      while (parts.length < size) parts.push("");
      parts[index] = val;
      this.$emit("input", parts.join(";"));
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-field {
  display: flex;
  flex-direction: column;
  gap: var(--sc-space-2);
  min-width: 0;
}

.gg-field--wide {
  grid-column: 1 / -1;
}

.gg-field__label {
  padding: 0 var(--sc-space-1);
  font-size: 16px;
  font-weight: var(--sc-weight-medium);
  line-height: 20px;
  letter-spacing: 0.16px;
  color: var(--sc-gg-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gg-field__box {
  display: flex;
  align-items: center;
  gap: var(--sc-space-1);
  height: 48px;
  padding: var(--sc-space-2) var(--sc-space-2) var(--sc-space-2) var(--sc-space-3);
  background: var(--sc-gg-surface-2);
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);

  &:focus-within {
    border-color: var(--sc-gg-accent);
  }
}

.gg-field__box--area {
  height: auto;
  align-items: stretch;
}

.gg-field__control {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--sc-gg-text-primary);
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.08px;
  text-overflow: ellipsis;
  outline: none;

  &::placeholder {
    color: var(--sc-gg-text-tertiary);
  }
}

.gg-field__control--upper {
  text-transform: uppercase;
}

.gg-field__control--area {
  resize: vertical;
  min-height: 56px;
  font-family: inherit;
}

.gg-field__control--select {
  appearance: none;
  cursor: pointer;
  // Native option lists are painted by the browser — this keeps them dark.
  color-scheme: dark;
}

.gg-field__chevron {
  display: inline-flex;
  flex-shrink: 0;
  pointer-events: none;

  .v-icon {
    color: var(--sc-gg-text-secondary) !important;
  }
}

.gg-field__trailing {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: var(--sc-radius-pill);
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  .v-icon {
    color: var(--sc-gg-accent) !important;
  }

  &:hover {
    background: rgba(var(--sc-gg-accent-rgb), 0.12);
  }
}

.gg-field__composite {
  display: flex;
  gap: var(--sc-space-2);

  .gg-field__box {
    flex: 1 1 0;
    min-width: 0;
  }
}

.gg-field__switch {
  display: flex;
  align-items: center;
  height: 48px;

  ::v-deep .v-input--switch {
    margin: 0;
    padding: 0;
  }
}
</style>
